import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getUsedDaysByType, getUsedDaysByLeaveYear, getLeaveYear, LEAVE_TYPES, calculateDaysBetween } from '../utils/vacationUtils';

const UserInfoBar = ({ 
  currentUser, 
  usedDays: propUsedDays,
  remainingDays,
  department, 
  vacationRequests,
  annualBalances = [],
  holidays = [],
  selectedLeaveYears = null,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const usedByType = useMemo(() => {
    return getUsedDaysByType(currentUser.id, vacationRequests, holidays);
  }, [currentUser.id, vacationRequests, holidays]);

  const currentLeaveYear = getLeaveYear(new Date());

  const activeYears = useMemo(() => {
    if (selectedLeaveYears && selectedLeaveYears.length > 0) return selectedLeaveYears;
    return [currentLeaveYear];
  }, [selectedLeaveYears, currentLeaveYear]);

  const isMultiYear = activeYears.length > 1;

  const getBalanceForYear = (year) => {
    if (!annualBalances || !annualBalances.length) return 0;
    const yearKey = `balance_${year}`;
    const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(currentUser.id));
    if (pivoted && pivoted[yearKey] !== undefined) return Number(pivoted[yearKey]);
    const raw = annualBalances.find(b =>
      Number(b.userId || b.user_id) === Number(currentUser.id) &&
      Number(b.cycle_start_year || b.year) === year
    );
    return raw?.total_allowed !== undefined ? Number(raw.total_allowed) : 0;
  };

  //   approved  = min(self-requested vacation days, allowance)  [capped]
  //   granted   = manager-granted, non-unpaid days
  //   overflow  = last year's (approved + granted - allowance), if positive
  //   "Χρησιμ."     shown = approved + overflow    <- matches "Εγκρίθηκαν"
  //   "Διαθέσιμες"  shown = allowance - approved - granted - overflow
  const {
  displayTotalDays,
  displayUsedDays,
  displayRemainingDays,
  overflowDays,
  grantedDays,
  grantedUnpaidDays,
  selfRequestedUnpaidDays,
  grantedWithinLimit,
  grantedExcess
} = useMemo(() => {
  let totalAllowed = 0;
  let totalApprovedShown = 0;   // approved (capped) + overflow -> "Εγκρίθηκαν"
  let totalRawApproved = 0;     // uncapped self-requested vacation
  let totalOverflow = 0;
  let totalGranted = 0;
  let totalGrantedUnpaid = 0;
  let totalSelfRequestedUnpaid = 0;
  let totalRemaining = 0;

  const cycleYearOf = (startDate) => {
    const parts = startDate.toString().split('T')[0].split('-').map(Number);
    return parts[1] - 1 < 3 ? parts[0] - 1 : parts[0];
  };

  const sumDays = (filterFn) => vacationRequests
    .filter(filterFn)
    .reduce((sum, req) =>
      sum + calculateDaysBetween(req.start_date || req.startDate, req.end_date || req.endDate, holidays), 0);

  const rawApprovedFor = (yr) => sumDays(req => {
    const isGranted = req.manager_granted || req.managerGranted;
    if (isGranted) return false;
    const reqType = (req.leave_type || req.leaveType || '').toLowerCase();
    if (reqType !== 'vacation') return false;
    const startDate = req.start_date || req.startDate;
    if (!startDate) return false;
    return Number(req.user_id || req.userId) === Number(currentUser.id) &&
      req.status === 'approved' &&
        cycleYearOf(startDate) === yr;
  });

  const grantedFor = (yr) => sumDays(req => {
    const isManagerGranted = req.manager_granted || req.managerGranted;
    const isUnpaid = (req.leave_type || req.leaveType) === 'unpaid';
    const startDate = req.start_date || req.startDate;
    if (!startDate) return false;
    return Number(req.user_id || req.userId) === Number(currentUser.id) &&
      req.status === 'approved' &&
        isManagerGranted && !isUnpaid &&
          cycleYearOf(startDate) === yr;
  });

  const overflowFor = (yr) => {
    const prevAllowed = getBalanceForYear(yr - 1);
    if (prevAllowed === 0) return 0;
    const prevUsed = getUsedDaysByLeaveYear(currentUser.id, vacationRequests, yr - 1, holidays);
    const prevOverflow = prevUsed - prevAllowed;
    return prevOverflow > 0 ? prevOverflow : 0;
  };

  activeYears.forEach(year => {
    const allowed = getBalanceForYear(year);
    const rawApproved = rawApprovedFor(year);
    const approved = allowed > 0 ? Math.min(rawApproved, allowed) : rawApproved;
    const granted = grantedFor(year);
    const overflow = overflowFor(year);

    const yearSelfRequestedUnpaid = sumDays(req => {
      const isGranted = req.manager_granted || req.managerGranted;
      if (isGranted) return false;
      const reqType = (req.leave_type || req.leaveType || '').toLowerCase();
      if (reqType !== 'unpaid') return false;
      const startDate = req.start_date || req.startDate;
      if (!startDate) return false;
      return Number(req.user_id || req.userId) === Number(currentUser.id) &&
        req.status === 'approved' &&
          cycleYearOf(startDate) === year;
    });

    const yearGrantedUnpaid = sumDays(req => {
      const isManagerGranted = req.manager_granted || req.managerGranted;
      const isUnpaid = (req.leave_type || req.leaveType) === 'unpaid';
      const startDate = req.start_date || req.startDate;
      if (!startDate) return false;
      return Number(req.user_id || req.userId) === Number(currentUser.id) &&
        req.status === 'approved' &&
          isManagerGranted && isUnpaid &&
            cycleYearOf(startDate) === year;
    });

    const remaining = allowed - approved - granted - overflow;

    totalAllowed              += allowed;
    totalApprovedShown        += approved + overflow;
    totalRawApproved          += rawApproved;
    totalOverflow             += overflow;
    totalGranted              += granted;
    totalGrantedUnpaid        += yearGrantedUnpaid;
    totalSelfRequestedUnpaid  += yearSelfRequestedUnpaid;
    totalRemaining            += remaining;
  });

  const finalTotal = totalAllowed || currentUser.total_days || currentUser.totalDays || 0;
  const availableBeforeGrant = Math.max(0, finalTotal - totalRawApproved - totalOverflow);
  const withinLimit = Math.max(0, Math.min(totalGranted, availableBeforeGrant));
  const excess = Math.max(0, totalGranted - availableBeforeGrant);

  return {
    displayTotalDays:     finalTotal,
    displayUsedDays:      totalApprovedShown,
    displayRemainingDays: Math.max(0, totalRemaining),
    overflowDays:         totalOverflow,
    grantedDays:          totalGranted,
    grantedUnpaidDays:    totalGrantedUnpaid,
    selfRequestedUnpaidDays: totalSelfRequestedUnpaid,
    grantedWithinLimit:   withinLimit,
    grantedExcess:        excess
  };
}, [currentUser.id, annualBalances, activeYears, vacationRequests, holidays, currentUser.total_days, currentUser.totalDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const remainingByType = {
    vacation: Math.max(0, displayRemainingDays),
    unjustified: Infinity,
    maternity: Math.max(0, (currentUser.maternity_days_total || currentUser.maternityDaysTotal || 119) - (usedByType.maternity || 0)),
    paternity: Math.max(0, (currentUser.paternity_days_total || currentUser.paternityDaysTotal || 14) - (usedByType.paternity || 0)),
    unpaid: Infinity,
    mandatory: 0
  };

  return (
    <div className="mb-6">
      <div className="p-4 bg-white dark:bg-jewel-bg rounded-lg border border-gray-200 dark:border-jewel-border shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div
              className="h-12 w-12 xs:h-10 xs:w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm xs:text-xs flex-shrink-0"
              style={{ backgroundColor: department?.color || '#3B82F6' }}
            >
              {currentUser.name ? currentUser.name.split(' ').map(n => n[0]).join('') : 'U'}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-jewel-text xs:text-sm">{currentUser.name}</h3>
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                <p className="text-sm xs:text-xs text-gray-600 dark:text-jewel-text">{department?.name || 'No Department'}</p>
                {department && (
                  <>
                    <span className="text-sm xs:text-xs text-gray-400 dark:text-jewel-text-muted">•</span>
                    <div className="flex items-center space-x-1">
                      <div
                        className="w-2 h-2 xs:w-1.5 xs:h-1.5 rounded-full"
                        style={{ backgroundColor: department.color }}
                      ></div>
                      <span className="text-sm xs:text-xs text-gray-500 dark:text-jewel-text-muted">{department.name}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="flex-1 md:flex-initial">
              {selectedLeaveYears && selectedLeaveYears.length > 0 && (
                <p className="text-[10px] text-blue-600 dark:text-jewel-teal-tint font-bold uppercase text-center mb-1">
                  {isMultiYear
                    ? `Σύνολο: ${activeYears.map(y => `${y}/${y+1}`).join(' + ')}`
                    : `Έτος ${activeYears[0]}/${activeYears[0] + 1}`}
                </p>
              )}
              <div className="grid grid-cols-3 gap-3 xs:gap-2">
                <div className="text-center relative group">
                  <p className="text-xs xs:text-[10px] text-gray-600 dark:text-jewel-text font-medium">
                    Διαθέσιμες
                    {overflowDays > 0 && <span className="text-orange-500 ml-0.5">*</span>}
                  </p>
                  <p className={`text-xl xs:text-lg font-bold ${displayRemainingDays <= 5 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {displayRemainingDays}
                  </p>
                  {overflowDays > 0 && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-52 bg-gray-900 dark:bg-jewel-surface text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-center shadow-xl">
                      {overflowDays} {overflowDays === 1 ? 'ημέρα χρεώθηκε' : 'ημέρες χρεώθηκαν'} από το προηγούμενο έτος
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900 dark:border-b-jewel-surface"></div>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs xs:text-[10px] text-gray-600 dark:text-jewel-text font-medium">Χρησιμ.</p>
                  <p className="text-xl xs:text-lg font-bold text-blue-600 dark:text-jewel-teal-tint">{displayUsedDays}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs xs:text-[10px] text-gray-600 dark:text-jewel-text font-medium">Σύνολο</p>
                  <p className="text-xl xs:text-lg font-bold text-gray-800 dark:text-jewel-text">{displayTotalDays}</p>
                </div>
              </div>
              {(grantedDays > 0 || grantedUnpaidDays > 0) && (
                <div className="mt-2 text-center space-y-0.5">
                  {grantedDays > 0 && (
                    <p className="text-xs xs:text-[10px] text-purple-600 dark:text-jewel-ruby-tint font-medium">{grantedDays} ήταν χορηγημένες</p>
                  )}
                  {grantedUnpaidDays > 0 && (
                    <p className="text-xs xs:text-[10px] text-orange-600 dark:text-orange-400 font-medium">{grantedUnpaidDays} χορηγήθηκαν ως άνευ αποδοχών</p>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-jewel-surface rounded-lg transition-colors flex-shrink-0"
            >
              {showDetails ? <ChevronUp className="h-5 w-5 text-gray-500 dark:text-jewel-text-muted" /> : <ChevronDown className="h-5 w-5 text-gray-500 dark:text-jewel-text-muted" />}
            </button>
          </div>
        </div>
      </div>
      {showDetails && (
        <div className="mt-2 p-4 xs:p-3 bg-white dark:bg-jewel-bg rounded-lg border border-gray-200 dark:border-jewel-border animate-in fade-in slide-in-from-top-2">
          <h4 className="font-semibold text-gray-800 dark:text-jewel-text mb-3">Ανάλυση Αδειών ανά Τύπο</h4>
          {grantedDays > 0 && (
            <div className="mb-4 p-3 bg-purple-50 dark:bg-jewel-ruby/20 border border-purple-200 dark:border-jewel-ruby rounded-lg">
              <h5 className="font-semibold text-purple-800 dark:text-jewel-ruby-tint mb-2 text-sm">🏢 Χορηγημένες Υποχρεωτικές Άδειες</h5>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-jewel-text">Σύνολο χορηγημένων:</span>
                  <span className="font-bold text-purple-600 dark:text-jewel-ruby-tint">{grantedDays} ημέρες</span>
                </div>
                {grantedExcess > 0 ? (
                  <div className="space-y-1">
                    {grantedWithinLimit > 0 && (
                      <div className="flex items-center space-x-2">
                        <div 
                          className="h-6 bg-green-500 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ width: `${Math.max(15, (grantedWithinLimit / grantedDays) * 100)}%` }}
                        >{grantedWithinLimit}</div>
                        <span className="text-[10px] text-gray-600 dark:text-jewel-text-muted">από διαθέσιμες</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <div 
                        className="h-6 bg-red-500 rounded flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ width: `${Math.max(15, (grantedExcess / grantedDays) * 100)}%` }}
                      >{grantedExcess}</div>
                      <span className="text-[10px] text-gray-600 dark:text-jewel-text-muted">υπέρβαση ορίου</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 h-8 bg-green-100 dark:bg-green-900/40 border border-green-200 dark:border-green-800 rounded flex items-center justify-center text-green-700 dark:text-green-400 text-xs font-semibold">
                      ✓ Όλες εντός διαθέσιμων ({grantedDays} ημέρες)
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {grantedUnpaidDays > 0 && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <h5 className="font-semibold text-orange-800 dark:text-orange-400 mb-2 text-sm">💼 Χορηγημένες Άδειες Άνευ Αποδοχών</h5>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-jewel-text">Σύνολο χορηγημένων άνευ αποδοχών:</span>
                <span className="font-bold text-orange-600 dark:text-orange-400">{grantedUnpaidDays} ημέρες</span>
              </div>
              <p className="text-[10px] text-gray-500 dark:text-jewel-text-muted mt-1.5">
                Δεν αφαιρούνται από το υπόλοιπο κανονικής άδειας.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 xs:gap-2">
            {Object.entries(LEAVE_TYPES)
              .filter(([type]) => {
                if (type === 'maternity' && currentUser.gender === 'male') return false;
                if (type === 'paternity' && currentUser.gender === 'female') return false;
                if (type === 'mandatory' && grantedDays === 0) return false;
                return true;
              })
              .map(([type, info]) => {
                const baseUsed = type === 'mandatory' ? grantedDays : (usedByType[type] || 0);
                
                const totalForType = type === 'vacation' 
                  ? displayTotalDays
                  : type === 'maternity' ? (currentUser.maternity_days_total || currentUser.maternityDaysTotal || 119)
                  : type === 'paternity' ? (currentUser.paternity_days_total || currentUser.paternityDaysTotal || 14)
                  : type === 'mandatory' ? grantedDays : 0;

                const used = type === 'vacation' ? displayUsedDays
                  : type === 'unpaid' ? selfRequestedUnpaidDays
                  : baseUsed;
                const remaining = type === 'mandatory' ? 0 : remainingByType[type];
                const total = (totalForType !== 0 && totalForType !== undefined) ? totalForType : '∞';
                const percentage = total !== '∞' && total > 0 ? (used / total) * 100 : 0;
                const hasNoLimit = type === 'unjustified' || type === 'unpaid';

                return (
                  <div key={type} className="p-3 xs:p-2 rounded-lg border border-gray-200 dark:border-jewel-border hover:shadow-md transition-shadow bg-gray-50/30 dark:bg-jewel-bg/30">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xl">{info.icon}</span>
                      <h5 className="text-xs font-bold text-gray-700 dark:text-jewel-text truncate">{info.label}</h5>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-jewel-text-muted">Σύνολο:</span>
                        <span className="font-bold text-gray-800 dark:text-jewel-text">{hasNoLimit ? '∞' : total}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-jewel-text-muted">Χρησιμ.:</span>
                        <span className="font-bold text-blue-600 dark:text-jewel-teal-tint">{used}</span>
                      </div>
                      {!hasNoLimit && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-jewel-text-muted">Απομένουν:</span>
                          <span className="font-bold text-green-600 dark:text-green-400">
                            {remaining === Infinity ? '∞' : Math.max(0, remaining)}
                          </span>
                        </div>
                      )}
                    </div>
                    {!hasNoLimit && type !== 'mandatory' && total > 0 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 dark:bg-jewel-surface rounded-full h-1.5">
                          <div 
                            className="h-1.5 rounded-full transition-all duration-500" 
                            style={{ 
                              width: `${Math.min(percentage, 100)}%`, 
                              backgroundColor: info.color || '#3B82F6' 
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInfoBar;